import { renderOptionalVariable } from "@climatepartner/utils";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Breadcrumbs as MuiBreadcrumbs, Link as MuiLink, Typography } from "@mui/material";
import { createContext, ReactElement, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, UNSAFE_DataRouterStateContext } from "react-router-dom";

import { rumActionNames } from "../../monitoring/rum-action-names";

/**
 * A route joins the breadcrumb trail by declaring this on its `handle`. The label lives next to the
 * path, so the trail follows the route tree: nesting a route under another puts the parent's crumb in
 * front of it, and no page lists its static ancestors by hand.
 */
export type CrumbHandle = { crumb: string };

export type BreadcrumbItem = {
  label: string;
  url: string;
  /** Runtime data such as a calculation name, which must not be sent for translation. */
  dontTranslate?: boolean;
};

export type BreadcrumbsProps = {
  /** Dynamic ancestors the route tree cannot carry (a calculation name), placed after the route crumbs. */
  items?: BreadcrumbItem[];
  /** The current page when it is dynamic; otherwise the last crumb of the trail is the current page. */
  currentPage?: string;
  translateCurrentPage?: boolean;
};

const crumbLabel = (handle: unknown): string | null => {
  if (handle && typeof handle === "object" && "crumb" in handle) {
    const { crumb } = handle as { crumb: unknown };
    if (typeof crumb === "string") return crumb;
  }
  return null;
};

/**
 * The crumbs of the matched routes, in order. Reads the state `useMatches` reads, minus its
 * invariant: a page rendered under a plain `MemoryRouter` (unit tests) has no route crumbs.
 */
const useRouteCrumbs = (): BreadcrumbItem[] => {
  const matches = useContext(UNSAFE_DataRouterStateContext)?.matches;

  return useMemo(
    () =>
      (matches ?? []).flatMap(({ route, pathname }) => {
        const label = crumbLabel(route.handle);
        return label === null ? [] : [{ label, url: pathname }];
      }),
    [matches]
  );
};

// A detail page rendered below a frame that owns the trail (the network areas) publishes its name
// here; the trail shows it as the current page. The default is a no-op so pages render without it.
const CrumbTitleContext = createContext<{ title: string | null; setTitle: (title: string | null) => void }>({
  title: null,
  setTitle: () => {},
});

export const BreadcrumbsProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [title, setTitle] = useState<string | null>(null);
  const value = useMemo(() => ({ title, setTitle }), [title]);
  return <CrumbTitleContext.Provider value={value}>{children}</CrumbTitleContext.Provider>;
};

/** Names the current page from runtime data; cleared when the page unmounts. */
export const useSetCrumbTitle = (title: string): void => {
  const { setTitle } = useContext(CrumbTitleContext);
  useEffect(() => {
    setTitle(title);
    return () => setTitle(null);
  }, [title, setTitle]);
};

type Crumb = { label: string; dontTranslate?: boolean };
const renderLabel = ({ label, dontTranslate }: Crumb): ReactNode =>
  dontTranslate ? renderOptionalVariable(label) : label;

/**
 * The one breadcrumb trail: route crumbs, then the dynamic `items`, then the current page. Renders
 * nothing when no route on the way here carries a crumb and nothing is passed.
 */
export const Breadcrumbs = ({
  items = [],
  currentPage,
  translateCurrentPage = true,
}: BreadcrumbsProps): ReactElement | null => {
  const routeCrumbs = useRouteCrumbs();
  const { title } = useContext(CrumbTitleContext);

  const trail = [...routeCrumbs, ...items];
  // An empty title is a detail page still loading its name: the trail's last crumb stands in.
  const dynamicCurrent: Crumb | undefined =
    currentPage !== undefined
      ? { label: currentPage, dontTranslate: !translateCurrentPage }
      : title
        ? { label: title, dontTranslate: true }
        : undefined;
  const links = dynamicCurrent ? trail : trail.slice(0, -1);
  const current = dynamicCurrent ?? trail.at(-1);

  if (current === undefined) return null;

  return (
    <MuiBreadcrumbs
      aria-label="breadcrumb"
      separator={<NavigateNextIcon fontSize="small" />}
      sx={{ mb: 2, color: "text.secondary" }}
      data-dd-action-name={rumActionNames.breadcrumbNavigated}
    >
      {links.map((crumb) => (
        <MuiLink key={crumb.url} component={RouterLink} to={crumb.url} color="inherit" underline="hover">
          {renderLabel(crumb)}
        </MuiLink>
      ))}
      <Typography sx={{ color: "text.primary", fontWeight: (theme) => theme.typography.fontWeightBold }}>
        {renderLabel(current)}
      </Typography>
    </MuiBreadcrumbs>
  );
};
